import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { kbRulesAPI, errMsg } from '../services/intelligenceApi';
import { RoleChips } from './IntelligenceKb';
import './Intelligence.css';

/* INTELLIGENCE → KB RULES: every rule in the knowledge base (the engine's catalog), with its sign-off state and
 * any change in flight. Opening a rule lets a KB reviewer approve it as it is or propose an edit; both become
 * KB factory jobs that run the engine gates and wait in KB REVIEW like any other draft. */

const SIGNOFF_LABEL = { signed: 'signed', unsigned: 'unsigned', grandfathered: 'grandfathered' };
const PAGE = 100;

export function SignoffBadge({ state }) {
  const cls = state === 'signed' ? 'ok' : state === 'unsigned' ? 'warn' : '';
  return <span className={`in-badge ${cls}`} title={state === 'grandfathered' ? 'No review block: shown to members, never signed under E19' : undefined}>{SIGNOFF_LABEL[state] || state}</span>;
}

function Chips({ label, value, options, counts, onChange }) {
  return (
    <div className="in-row" style={{ marginBottom: 6 }}>
      <span className="in-muted" style={{ minWidth: 92 }}>{label}</span>
      <div className="in-chips">
        <button type="button" className={`in-chip ${!value ? 'on' : ''}`} onClick={() => onChange('')}>all</button>
        {options.map(([v, text]) => (
          <button key={v} type="button" className={`in-chip ${value === v ? 'on' : ''}`} onClick={() => onChange(value === v ? '' : v)}>
            {text} <span className="in-muted" style={{ color: 'inherit', opacity: 0.75 }}>{(counts || {})[v] || 0}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function readParams() {
  const p = new URLSearchParams(window.location.search);
  const out = {};
  for (const k of ['q', 'jurisdiction', 'kind', 'confidence', 'quote', 'signoff', 'pending']) if (p.get(k)) out[k] = p.get(k);
  return out;
}

export default function IntelligenceKbRules() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(readParams);
  const [q, setQ] = useState(filters.q || '');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (fresh) => {
    setLoading(true);
    try {
      const r = await kbRulesAPI.list({ ...filters, limit: PAGE, offset, fresh: fresh ? 1 : undefined });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => {
    const next = { ...filters, [k]: v || undefined };
    Object.keys(next).forEach((x) => next[x] === undefined && delete next[x]);
    setFilters(next);
    setOffset(0);
    const qs = new URLSearchParams(next).toString();
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
  };
  useEffect(() => {
    const t = setTimeout(() => { if ((filters.q || '') !== q.trim()) set('q', q.trim()); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const c = data?.counts;
  const jurs = c ? Object.entries(c.jurisdiction).sort((a, b) => b[1] - a[1]) : [];
  const kinds = c ? Object.keys(c.kind).sort() : [];
  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">KB RULES</h1>
            <p className="page-subtitle">Every rule in the knowledge base. Approve one as it is or propose an edit: each goes through the engine gates and the KB REVIEW queue before it is published.</p>
          </div>
          <RoleChips viewer={data?.viewer} />
        </div>
        {error && <div className="in-banner bad">{error}</div>}
        <div className="in-card">
          <div className="in-row" style={{ marginBottom: 10 }}>
            <input className="in-input" style={{ flex: 1, minWidth: 220 }} placeholder="Search id, title, plain language, keywords" value={q}
              onChange={(e) => setQ(e.target.value)} aria-label="Search rules" />
            <select className="in-select" value={filters.jurisdiction || ''} onChange={(e) => set('jurisdiction', e.target.value)} aria-label="Jurisdiction">
              <option value="">all jurisdictions</option>
              {jurs.map(([j, n]) => <option key={j} value={j}>{j} ({n})</option>)}
            </select>
            <select className="in-select" value={filters.kind || ''} onChange={(e) => set('kind', e.target.value)} aria-label="Kind">
              <option value="">all kinds</option>
              {kinds.map((k) => <option key={k} value={k}>{k} ({c.kind[k]})</option>)}
            </select>
            <button type="button" className="in-btn small" onClick={() => load(true)}>Refresh</button>
          </div>
          <Chips label="Confidence" value={filters.confidence} counts={c?.confidence} onChange={(v) => set('confidence', v)}
            options={[['high', 'high'], ['moderate', 'moderate'], ['low', 'low']]} />
          <Chips label="Quote" value={filters.quote} counts={c?.quote} onChange={(v) => set('quote', v)} options={[['yes', 'has quote'], ['no', 'no quote']]} />
          <Chips label="Sign-off" value={filters.signoff} counts={c?.signoff} onChange={(v) => set('signoff', v)}
            options={[['signed', 'signed'], ['unsigned', 'unsigned'], ['grandfathered', 'grandfathered']]} />
          <Chips label="Pending edit" value={filters.pending} counts={c?.pending} onChange={(v) => set('pending', v)} options={[['yes', 'pending'], ['no', 'none']]} />
          {c && <p className="in-muted" style={{ margin: '8px 0 0' }}>{c.matching} of {c.total} rules{data.kb_version ? ` · KB ${String(data.kb_version).slice(7, 19)}` : ''}</p>}
        </div>

        <div className="in-card">
          {loading && !data ? <div className="in-empty">Loading the KB…</div> : !data?.items.length ? <div className="in-empty">No rules match.</div> : (
            <div className="in-table-wrap">
              <table className="in-table">
                <thead><tr><th>Rule</th><th>Jurisdiction</th><th>Kind</th><th>Confidence</th><th>Quote</th><th>Sign-off</th><th>Pending</th></tr></thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.id} className="clickable" onClick={() => navigate(`/intelligence/kb/rules/${encodeURIComponent(r.id)}`)}>
                      <td style={{ maxWidth: 520 }}>
                        <div className="in-mono">{r.id}</div>
                        <div><b>{r.title}</b></div>
                        {r.plain_language && <div className="in-muted">{r.plain_language}</div>}
                      </td>
                      <td>{(r.jurisdiction || []).join(', ')}</td>
                      <td className="in-muted">{r.record_kind}</td>
                      <td><span className={`in-badge ${r.confidence === 'high' ? 'ok' : ''}`}>{r.confidence || '—'}</span></td>
                      <td>{r.has_quote ? <span className="in-badge ok">quote</span> : <span className="in-muted">—</span>}</td>
                      <td><SignoffBadge state={r.signoff} />{r.overridden && <div className="in-muted">records.d</div>}</td>
                      <td>{r.pending ? <span className="in-badge warn">{r.pending.draft_status || r.pending.job_status}</span> : <span className="in-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {c && c.matching > PAGE && (
            <div className="in-pager">
              <button type="button" className="in-btn small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
              <span className="in-muted">{offset + 1}–{Math.min(offset + PAGE, c.matching)} of {c.matching}</span>
              <button type="button" className="in-btn small" disabled={offset + PAGE >= c.matching} onClick={() => setOffset(offset + PAGE)}>Next</button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
